import React, { useState, useEffect, useContext } from 'react';
import { CheckCircle, Clock, CreditCard, Link, QrCode, RotateCcw } from 'lucide-react';
import MyContext from '../../../MyContext';
import axios from 'axios';

const PaymentScreen = ({ 
  tenderedAmount, 
  orderId, 
  userID, 
  custName, 
  custMobile, 
  setShowUPIModal,
  isConnected,
  onConnect,
  writerRef ,
  cart,
  placeOrderApi,
  apiLoader
}) => {
  const { baseUrl, config } = useContext(MyContext);

  const [upiLink, setUpiLink] = useState('');
  const [statusMessage, setStatusMessage] = useState('🔄 Generating UPI link...');
  const [stage, setStage] = useState('init'); // init | waiting | success
    const [paymentStatus, setPaymentStatus] = useState('');

  useEffect(() => {
    const fetchUPIIntent = async () => {
      try {
        const requestBody = { userid: userID, orderId, custName, custMobile };
        const response = await axios.post(`${baseUrl}/api/Order/v2.0/generatePaymentIntent`, requestBody, config);
        const paymentLink = response?.data?.response?.paymentLink;

        if (response.data.responseCode === '00' && paymentLink) {
          setUpiLink(paymentLink);
          setStatusMessage(' UPI link generated');
        } else {
          throw new Error(response.data.responseMessage || 'UPI generation failed');
        }
      } catch (err) {
        setStatusMessage(` Failed to generate UPI: ${err.message}`);
      }
    };

    fetchUPIIntent();
  }, [baseUrl, config, userID, orderId, custName, custMobile]);
  




  // 2️⃣ API to Check Payment Status
 const paymentCallback = async () => {
  try {
    const requestBody = { orderId };
    const response = await axios.post(
      `${baseUrl}/api/Order/v1.0/getPosOrderPaymentDetails`,
      requestBody,
      config
    );

    if (response.data.responseCode === '00') {
      const paymentData = response.data.response;
      setPaymentStatus(paymentData);

      // ✅ When payment successful
      if (paymentData[0]?.payment_status === 'SUCCESS') {
        setStage('success');
        setStatusMessage('✅ Payment Received Successfully!');
        clearInterval(window.paymentCheckInterval);

        // Notify hardware
        if (writerRef?.current) {
          await writerRef.current.write('success\n');
        }

        // ✅ Auto reset after 5s
        setTimeout(() => {
          handleReset();
        }, 5000);
      } else {
        setStatusMessage('⏳ Waiting for payment confirmation...');
      }
    } else {
      throw new Error(response.data.responseMessage || 'Failed to check payment');
    }
  } catch (err) {
    setStatusMessage(`⚠️ Error checking payment: ${err.message}`);
  }
};

  // 3️⃣ Automatically start checking payment every 5 seconds after UPI link is generated
  useEffect(() => {
    if (upiLink) {
      // Start polling every 5 seconds
      window.paymentCheckInterval = setInterval(paymentCallback, 5000);
      console.log('⏱️ Started auto payment check...');

      return () => {
        console.log('🧹 Stopping auto payment check...');
        clearInterval(window.paymentCheckInterval);
      };
    }
  }, [upiLink]);

const sendIfReady = async () => {
  if (upiLink && tenderedAmount && writerRef?.current) {
    try {
      // Parse UPI link
      const url = new URL(upiLink);
      const params = new URLSearchParams(url.search);

      const pa = params.get('pa'); // Payee UPI ID
      const pn = params.get('pn') || 'BumppyPayment'; // Optional Payee Name / Txn Note fallback
      const tn = params.get('tn') || 'Payment'; // Txn Note
      const am = tenderedAmount; // Amount to send

      const dataToSend = `${pa}::${tn}::${am}`;

      // Send to Arduino via Serial writer
      await writerRef.current.write(dataToSend + '\n');

      console.log('✅ Dynamic UPI QR sent to Arduino:', dataToSend);

      setStage('waiting');
      setStatusMessage('✅ QR sent, waiting for payment...');
    } catch (err) {
      console.error('❌ Failed to send QR:', err);
      setStatusMessage(`❌ Failed to send QR: ${err.message}`);
    }
  } else {
    setStatusMessage('⚠️ Missing UPI Link, Amount, or Connection');
  }
};



  const handleShowSuccess = async () => {
    setStage('success');
    setStatusMessage(' Payment marked as success');
    try {
      if (writerRef?.current) {
        await writerRef.current.write('success\n');
      }
    } catch (err) {
      setStatusMessage(` Failed to write success: ${err.message}`);
    }
  };

  const handleCancel = async () => {
    setStage('init');
    setStatusMessage(' Payment cancelled');
    try {
      if (writerRef?.current) {
        await writerRef.current.write('cancel\n');
      }
    } catch (err) {
      setStatusMessage(` Failed to notify cancel: ${err.message}`);
    }
  };

  const handleReset = () => {
    setStage('init');
    setUpiLink('');
    setStatusMessage(' Resetting for new payment...');
    setShowUPIModal(false);
    handleCancel();
  };

// 2️⃣ Automatically call sendIfReady() when UPI link and connection are ready
useEffect(() => {
  const autoSendQR = async () => {
    if (upiLink && tenderedAmount && writerRef?.current) {
      console.log('⚡ Auto-triggering Show QR as UPI link generated...');
      await sendIfReady();
    } else if (upiLink && !writerRef?.current) {
      console.warn('⚠️ UPI link ready, but device not connected yet');
    }
  };

  autoSendQR();
}, [upiLink, tenderedAmount, writerRef]);

// ✅ Auto trigger success screen when payment is done
useEffect(() => {
  if (
    paymentStatus &&
    Array.isArray(paymentStatus) &&
    paymentStatus[0]?.payment_status === 'paid'
  ) {
    console.log('🎉 Auto-triggering Thank You screen (payment SUCCESS)');
    handleShowSuccess(); // same behavior as button click
  }
}, [paymentStatus]);
// ✅ Wait 5 seconds then reset
useEffect(() => {
  if (stage === 'success') {
    console.log('⏳ Waiting 5 seconds before auto reset...');
    const timer = setTimeout(() => {
      console.log('🔁 Auto-reset triggered after success');
      handleReset();
    }, 5000);
    return () => clearTimeout(timer);
  }
}, [stage]);


  const styles = {
    screen: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center' },
    title: { fontSize: 28, fontWeight: 'bold', margin: '16px 0' },
    amount: { fontSize: 36, fontWeight: 'bold', margin: '16px 0' },
    buttonRow: { display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', marginTop: 20 },
    button: { padding: '10px 18px', fontSize: 16, borderRadius: 8, cursor: 'pointer', border: 'none' },
    primaryBtn: { background: '#007bff', color: '#fff' },
    successBtn: { background: '#28a745', color: '#fff' },
    cancelBtn: { background: '#dc3545', color: '#fff' },
    resetBtn: { background: '#888', color: '#fff' },
    connectBtn: { background: '#17a2b8', color: '#fff' },
  };

  if (stage === 'success') {
    return (
      <div style={styles.screen}>
        <CheckCircle size={72} />
        <div style={styles.title}>Payment Successful</div>
        <div style={styles.amount}>₹{tenderedAmount}</div>
        <div style={styles.buttonRow}>
          <button style={{ ...styles.button, ...styles.resetBtn }} onClick={handleReset}>
            <RotateCcw size={16} /> Reset / Close
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'waiting') {
    return (
      <div style={styles.screen}>
        <Clock size={72} />
        <div style={styles.title}>Waiting for Payment</div>
        <h2 style={styles.amount}>₹{tenderedAmount}</h2>

<div
  style={{
    marginTop: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    fontSize: 18,
    fontWeight: '600',
  }}
>
  <span style={{ color: '#555' }}>Payment Status:</span>
  <span
    style={{
      padding: '8px 18px',
      borderRadius: 25,
      backgroundColor:
        paymentStatus[0]?.payment_status === 'paid'
          ? '#d1fae5' // Light green
          : '#fee2e2', // Light red for unpaid
      color:
        paymentStatus[0]?.payment_status === 'paid'
          ? '#065f46' // Dark green text
          : '#991b1b', // Dark red text
      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
      minWidth: 130,
      textAlign: 'center',
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      fontWeight: '700',
      transition: 'all 0.3s ease-in-out',
    }}
  >
    {paymentStatus[0]?.payment_status === 'paid' ? 'PAID' : 'UNPAID'}
  </span>
</div>
        <div style={styles.buttonRow}>
          <button style={{ ...styles.button, ...styles.cancelBtn }} onClick={handleCancel}>
            Cancel
          </button>
          <button style={{ ...styles.button, ...styles.successBtn }} onClick={handleShowSuccess}>
             Mark Success
          </button>
           <button style={{ ...styles.button, ...styles.primaryBtn }} onClick={paymentCallback}>
            <RotateCcw size={16} /> Check Payment
          </button>
          <button style={{ ...styles.button, ...styles.resetBtn }} onClick={handleReset}>
            <RotateCcw size={16} /> Reset / Close
          </button>
         


        </div>
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <CreditCard size={48} />
      <div style={styles.title}>Bumppy QR Billing</div>
      <div>{statusMessage}</div>
      <div style={styles.buttonRow}>
        {!isConnected ? (
          <button style={{ ...styles.button, ...styles.connectBtn }} onClick={onConnect}>
            <Link size={16} /> Connect Machine
          </button>
        ) : (
          <button style={{ ...styles.button, ...styles.primaryBtn }} onClick={sendIfReady}>
            <QrCode size={16} /> Show QR
          </button>
        )}
        <button style={{ ...styles.button, ...styles.resetBtn }} onClick={handleReset}>
          <RotateCcw size={16} /> Reset / Close
        </button>

 <button style={{ ...styles.button, ...styles.resetBtn }} onClick={paymentCallback}>
            <RotateCcw size={16} /> Check Payment
          </button>
      
      </div>
    </div>
  );
};

export default PaymentScreen;
